/**
 * Sorts strings alphabetically, but also takes into account numbers in the strings.
 *
 * @remarks
 * This function is useful for sorting strings that contain numbers, e.g. "Item 1", "Item 2", "Item 10".
 *
 */
export function sortAlphanumeric(a: string, b: string): number {
    const regex = /(?<letters>\D+)|(?<numbers>\d+)/g; // Regular expression with named capture groups
    const aMatch = a.match(regex);
    const bMatch = b.match(regex);

    // Fallback if either string doesn't match the regex
    if (!aMatch || !bMatch) {
        return a.localeCompare(b); // Fall back to regular alphabetical sorting
    }

    const aLetters = aMatch[0];
    const bLetters = bMatch[0];

    // Compare letter parts first
    const letterComparison = aLetters.localeCompare(bLetters);

    // If letters are different, return the result
    if (letterComparison !== 0) {
        return letterComparison;
    }

    // If letters are the same, compare numbers
    const numA = parseInt(aMatch[1]);
    const numB = parseInt(bMatch[1]);

    return numA - numB;
}

/**
 * Sorts an array of objects by their sequence property in ascending order.
 *
 * @remarks
 * This function is useful for sorting arrays of objects that have a sequence property,
 * e.g. attributes, items, etc.
 *
 * @param array - Array of objects with a sequence property
 * @param descending - If true, sorts in descending order (default: false)
 * @returns A new sorted array
 */
export function sortBySequence<T extends { sequence: number }>(
    array: T[],
    descending = false,
): T[] {
    return array.slice().sort((a, b) => {
        return descending ? b.sequence - a.sequence : a.sequence - b.sequence;
    });
}
